import React, {useCallback, useEffect, useMemo, useState} from 'react'
import useTranslate from "../../../modules/local/useTranslate";
import {FaRegTrashCan} from "react-icons/fa6";
import {Button} from "antd";
import {FiEdit3} from "react-icons/fi";
import classNames from "classnames";

// --- CONFIG & UTILITIES ---

/**
 * Utility function to convert a decimal score (e.g., 9.25) back to its base (9) and fraction (0.25).
 * @param {number} decimalScore
 * @returns {{base: number, fraction: number}}
 */
const decimalToParts = (decimalScore) => {
    if (decimalScore === null) return {base: null, fraction: null};
    const base = Math.floor(decimalScore);
    const fraction = decimalScore - base;
    return {base, fraction};
};

const GradeNumberInput = ({defaultValue, onChange, maxScore = 10}) => {
    const t = useTranslate();
    const [isSelectingBase, setIsSelectingBase] = useState(true);
    const [tempBase, setTempBase] = useState(null);

    // Synchronize local state with external prop value
    useEffect(() => {
        if (defaultValue === null) {
            setTempBase(null);
            setIsSelectingBase(true);
        } else {
            const {base, fraction} = decimalToParts(defaultValue);
            setTempBase(base);
            // If a value exists, we skip base selection initially but still show the score.
            // When the user clicks to edit, it will reset to base selection if needed.
        }
    }, [defaultValue]);

    const {base, fraction} = useMemo(() => decimalToParts(defaultValue), [defaultValue]);

    const handleBaseSelect = useCallback((score) => {
        setTempBase(score);
        setIsSelectingBase(false);

        // If the current score is already set, we immediately calculate the new score
        // by keeping the old fraction. If no score is set, we default to 0.00 fraction.
        const currentFraction = defaultValue !== null ? (defaultValue - Math.floor(defaultValue)) : 0;
        onChange(score + currentFraction);
    }, [onChange, defaultValue]);

    const handleFractionSelect = useCallback((frac) => {
        if (tempBase === null) return; // Should not happen

        const newScore = tempBase + frac;
        onChange(newScore);
        setIsSelectingBase(false); // Stay in fraction view until reset/edit
    }, [tempBase, onChange]);

    const handleClear = useCallback(() => {
        onChange(null);
        setTempBase(null);
        setIsSelectingBase(true);
    }, [onChange]);

    const handleEdit = useCallback(() => {
        setIsSelectingBase(true);
    }, []);

    // Scores array: [1, 2, ..., maxScore]
    const scores = useMemo(() => Array.from({length: maxScore}, (_, i) => i + 1), [maxScore]);

    // Fraction display and value mapping
    const fractions = useMemo(() => [
        {display: <span className="flex gap-1 text-lg">{tempBase}</span>, value: 0.00},
        {
            display: <span className="flex gap-1"><span className="font-bold text-xl">{tempBase}</span><span
                className="font-medium text-xs">1/4</span></span>, value: 0.25
        },
        {
            display: <span className="flex gap-1"><span className="font-bold text-xl">{tempBase}</span><span
                className="font-medium text-xs">2/4</span></span>, value: 0.5
        },
        {
            display: <span className="flex gap-1"><span className="font-bold text-xl">{tempBase}</span><span
                className="font-medium text-xs">3/4</span></span>, value: 0.75
        },
    ], [tempBase]);

    const currentFractionDisplay = useMemo(() => {
        const found = fractions.find(f => Math.abs(f.value - fraction) < 0.001);
        return found ? found.display : '—';
    }, [fraction, fractions]);

    const DisplayScore = (
        <div className="flex items-center justify-between p-4 border border-color-50 shadow-md rounded-xl transition duration-150">
            <div className="flex flex-col">
                <span className="text-sm text-color-300 tracking-wider">{t("Selected Grade")}</span>
                <span className="text-3xl font-bold text-color-000">
                    {defaultValue === null ? '—' : defaultValue.toFixed(2)}
                </span>
            </div>
            <div className="flex space-x-2">
                {defaultValue !== null && (
                    <Button
                        color="default" variant="filled"
                        onClick={handleEdit}
                        className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-600 shadow-md rounded-md no-border transition duration-150"
                        title={t("edit grade")}
                        icon={<FiEdit3 />}
                    />
                )}
                <Button
                    color="primary" variant="filled"
                    onClick={handleClear}
                    className="p-2 bg-primary-50 hover:bg-primary-100 text-primary-600 shadow-md rounded-md no-border transition duration-150"
                    title={t("clear grade")}
                    icon={<FaRegTrashCan />}
                />
            </div>
        </div>
    );

    const BaseScoreSelector = (
        <div className="p-4 bg-white rounded-xl shadow-lg border border-indigo-100">
            <h3 className="text-base font-semibold text-gray-700 mb-3">
                1. {t("Select Base Grade")}
            </h3>
            <div className="grid grid-cols-5 md:grid-cols-10 gap-3">
                {scores.map((s) => {
                    const isSelected = tempBase === s;
                    return (
                        <button
                            key={s}
                            onClick={() => handleBaseSelect(s)}
                            className={classNames(
                                "w-full py-2 flex flex-center font-bold shadow rounded-lg transition-all duration-200 active:scale-95",
                                isSelected ?
                                    'bg-indigo-500 text-white ring-4' :
                                    'background text-color-200 hover:bg-indigo-50 hover:ring-2 hover:ring-indigo-200'
                            )}>
                            {s}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    const FractionSelector = (
        <div className="p-4 bg-white rounded-xl shadow-lg border border-indigo-100 mt-4">
            <h3 className="text-base font-semibold text-gray-700 mb-3">
                2. {t("Select Modifier")}
            </h3>
            <div className="flex justify-between gap-x-3">
                {fractions.map((f) => {
                    const isSelected = Math.abs(f.value - fraction) < 0.001 && tempBase === base;
                    return (
                        <button
                            key={f.value}
                            onClick={() => handleFractionSelect(f.value)}
                            className={classNames(
                                "py-3 flex flex-center flex-1 text-sm font-semibold shadow rounded-lg transition-all duration-200 active:scale-95",
                                isSelected
                                    ? 'bg-indigo-500 text-white'
                                    : 'background text-color-200 hover:bg-indigo-50 hover:ring-2 hover:ring-indigo-200'
                                )}>
                            {f.display}
                        </button>
                    );
                })}
            </div>
            <div
                onClick={handleEdit}
                className="mt-4 py-2 text-center text-sm font-medium text-indigo-500 hover:text-primary transition active:scale-95 cursor-pointer">
                {t("Edit Base Grade")} ({tempBase})
            </div>
        </div>
    );

    return (
        <div className="w-full space-y-4">
            {DisplayScore}
            {isSelectingBase || tempBase === null
                ? BaseScoreSelector
                : FractionSelector
            }
        </div>
    );
};

export default GradeNumberInput
